import React from 'react';
import { IconButton } from '@noths/polaris-design-system-core-components';

import { getPages } from 'src/components/organisms/ProductReviews/helpers/paginationHelper';
import * as copy from '../constants/copy';
import * as styles from './ReviewsPagination.styles';

const ELLIPSIS = '...';

export interface ReviewsPaginationProps {
  currentPage: number;
  onPageChange: (page: number, trigger: 'next' | 'page' | 'previous') => void;
  totalPages: number;
}

/**
 * Page controls for the drawer's review list. Reuses `getPages` for the windowing and
 * ellipsis rules, but not the old `Pagination` component, whose props are shaped around
 * rar-data-api, the service this replaces.
 */
export const ReviewsPagination = ({
  currentPage,
  onPageChange,
  totalPages,
}: ReviewsPaginationProps) => {
  if (totalPages <= 1) {
    return null;
  }

  const pages = getPages(currentPage, totalPages);

  return (
    <nav aria-label={copy.PAGINATION_LABEL}>
      <ul css={styles.list} data-testid="reviews-pagination">
        <li>
          <IconButton
            aria-label={copy.PREVIOUS_PAGE}
            compact
            disabled={currentPage === 1}
            icon="actionBack"
            onClick={() => onPageChange(currentPage - 1, 'previous')}
          />
        </li>

        {pages.map((page, index) =>
          page === ELLIPSIS ? (
            /* getPages emits at most one ellipsis per side, so the side is a stable key. */
            <li
              aria-hidden
              css={styles.ellipsis}
              key={index < pages.length / 2 ? 'ellipsis-start' : 'ellipsis-end'}
            >
              {ELLIPSIS}
            </li>
          ) : (
            <li key={`page-${page}`}>
              <button
                aria-current={currentPage === page ? 'page' : undefined}
                aria-label={`Page ${page}`}
                css={styles.pageButton}
                data-is-active={currentPage === page}
                onClick={() => onPageChange(page, 'page')}
                type="button"
              >
                {page}
              </button>
            </li>
          ),
        )}

        <li>
          <IconButton
            aria-label={copy.NEXT_PAGE}
            compact
            disabled={currentPage === totalPages}
            icon="actionForward"
            onClick={() => onPageChange(currentPage + 1, 'next')}
          />
        </li>
      </ul>
    </nav>
  );
};

ReviewsPagination.displayName = 'ReviewsPagination';
